import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

type FooterOwner = 'inbox' | 'conversation' | 'new-post-text-focus' | null

interface HeaderLayoutContextProps {
  title: string | ReactNode
  setTitle: (title: string | ReactNode) => void

  handleGoBack?: () => void
  setHandleGoBack: (cb?: () => void) => void

  hideBackButton: boolean
  setHideBackButton: (val: boolean) => void

  hideHeader: boolean
  setHideHeader: (val: boolean) => void

  headerRightEl?: ReactNode
  setHeaderRightEl: (el?: ReactNode) => void

  hideFooter: boolean
  setHideFooter: (val: boolean) => void

  footerOwner: FooterOwner
  setFooterHidden: (val: boolean, owner: Exclude<FooterOwner, null>) => void
  clearFooterHidden: (owner: Exclude<FooterOwner, null>) => void

  backgroundColor?: string
  setBackgroundColor: (color?: string) => void
}

const HeaderLayoutContext = createContext<HeaderLayoutContextProps | undefined>(undefined)

export const HeaderLayoutProvider = ({ children }: { children: ReactNode }) => {
  const [title, setTitle] = useState<string | ReactNode>('')
  const [handleGoBack, setHandleGoBack] = useState<(() => void) | undefined>(undefined)
  const [hideBackButton, setHideBackButton] = useState(false)
  const [hideHeader, setHideHeader] = useState(false)
  const [headerRightEl, setHeaderRightEl] = useState<ReactNode>(undefined)

  const [hideFooter, setHideFooterState] = useState(false)
  const [footerOwner, setFooterOwner] = useState<FooterOwner>(null)

  const [backgroundColor, setBackgroundColor] = useState<string | undefined>()

  const setHideFooter = useCallback((val: boolean) => {
    setHideFooterState(val)

    if (!val) {
      setFooterOwner(null)
    }
  }, [])

  const setFooterHidden = useCallback(
    (val: boolean, owner: Exclude<FooterOwner, null>) => {
      if (val) {
        setHideFooterState(true)
        setFooterOwner(owner)
      } else {
        setHideFooterState(false)
        setFooterOwner(null)
      }
    },
    []
  )

  const clearFooterHidden = useCallback((owner: Exclude<FooterOwner, null>) => {
    setFooterOwner((currentOwner) => {
      if (currentOwner !== owner) {
        return currentOwner
      }

      setHideFooterState(false)
      return null
    })
  }, [])

  const value = useMemo(
    () => ({
      title,
      setTitle,

      handleGoBack,
      setHandleGoBack,

      hideBackButton,
      setHideBackButton,

      hideHeader,
      setHideHeader,

      headerRightEl,
      setHeaderRightEl,

      hideFooter,
      setHideFooter,

      footerOwner,
      setFooterHidden,
      clearFooterHidden,

      backgroundColor,
      setBackgroundColor,
    }),
    [
      title,
      handleGoBack,
      hideBackButton,
      hideHeader,
      headerRightEl,
      hideFooter,
      setHideFooter,
      footerOwner,
      setFooterHidden,
      clearFooterHidden,
      backgroundColor,
    ]
  )

  return (
    <HeaderLayoutContext.Provider value={value}>
      {children}
    </HeaderLayoutContext.Provider>
  )
}

export const useHeaderLayout = (): HeaderLayoutContextProps => {
  const context = useContext(HeaderLayoutContext)

  if (!context) {
    throw new Error('useHeaderLayout must be used within a HeaderLayoutProvider')
  }

  return context
}